import { Phone, Users, LogOut, DoorOpen } from "lucide-react";
import { useState } from "react";
import { ControlButton } from "../common/ControlButton";
import { Button } from "@/clientWeb/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/clientWeb/components/ui/vcDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";

interface LeaveButtonProps {
  onEndWebinarForAll?: () => void;
  onLeaveWebinar?: () => void;
  onEndMeetingForAll?: () => void;
  onLeaveMeeting?: () => void;
  /**
   * In a breakout room, so "leave" is two different things.
   *
   * Leaving the breakout returns them to the main room; leaving the meeting
   * ends their session. Both belong here, because this is the button somebody
   * reaches for when they want out of where they are — and with only one of
   * them present, stepping out of a breakout meant dropping the meeting.
   */
  inBreakoutRoom?: boolean;
  onLeaveBreakoutRoom?: () => void;
  isWebinar?: boolean;
  /**
   * Ending for everyone is a moderator action. An attendee gets a single
   * "Leave" item instead of a two-option menu, since offering them an action
   * the server will reject is just a confusing dead end.
   */
  canEndForAll?: boolean;
  /** The phone bar's 44 px size, matching the controls beside it. */
  compact?: boolean;
}

export function LeaveButton({
  onEndWebinarForAll,
  onLeaveWebinar,
  onEndMeetingForAll,
  onLeaveMeeting,
  inBreakoutRoom = false,
  onLeaveBreakoutRoom,
  isWebinar = false,
  canEndForAll = false,
  compact = false,
}: LeaveButtonProps) {
  const t = useTranslation();
  // Ending for everyone used to fire straight off the menu item. It is the one
  // action in this menu that reaches past the person taking it — everybody
  // else is disconnected, mid-sentence, with no undo — and it sits directly
  // above "Leave meeting", which is the one people actually mean. A misclick
  // cost the whole meeting.
  const [confirmEnd, setConfirmEnd] = useState<"meeting" | "webinar" | null>(null);

  const confirmEndForAll = () => {
    if (confirmEnd === "webinar") onEndWebinarForAll?.();
    else onEndMeetingForAll?.();
    setConfirmEnd(null);
  };

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div>
          <ControlButton danger compact={compact} onClick={() => { }} label={t("controlBar.leave")}>
            <Phone className="h-5 w-5 text-white" />
          </ControlButton>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="min-w-[200px]">
        {isWebinar ? (
          <>
            {canEndForAll && (
              <DropdownMenuItem
                className="gap-3 text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
                onSelect={() => setConfirmEnd("webinar")}
              >
                <Users className="h-4 w-4 dark:text-foreground" />
                <span>{t("controlBar.endWebinarForAll")}</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="gap-3"
              onSelect={() => onLeaveWebinar?.()}
            >
              <LogOut className="h-4 w-4 dark:text-foreground" />
              <span>{t("controlBar.leaveWebinar")}</span>
            </DropdownMenuItem>
          </>
        ) : (
          <>
            {canEndForAll && (
              <DropdownMenuItem
                className="gap-3 text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
                onSelect={() => setConfirmEnd("meeting")}
              >
                <Users className="h-4 w-4" />
                <span>{t("controlBar.endMeetingForAll")}</span>
              </DropdownMenuItem>
            )}
            {/* Listed first: from inside a breakout it is the likelier
                intention, and it is the reversible one of the two. */}
            {inBreakoutRoom && onLeaveBreakoutRoom && (
              <DropdownMenuItem
                className="gap-3"
                onSelect={() => onLeaveBreakoutRoom()}
              >
                <DoorOpen className="h-4 w-4" />
                <span>{t("controlBar.leaveBreakoutRoom")}</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="gap-3"
              onSelect={() => onLeaveMeeting?.()}
            >
              <LogOut className="h-4 w-4" />
              <span>{t("controlBar.leaveMeeting")}</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>

    <Dialog open={confirmEnd !== null} onOpenChange={(o) => !o && setConfirmEnd(null)}>
      <DialogContent maxWidthClass="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>
            {confirmEnd === "webinar"
              ? t("controlBar.endWebinarForAll")
              : t("controlBar.endMeetingForAll")}
          </DialogTitle>
          <DialogDescription>
            {confirmEnd === "webinar"
              ? t("controlBar.endWebinarForAllConfirm")
              : t("controlBar.endMeetingForAllConfirm")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setConfirmEnd(null)}>
            {t("controlBar.cancel")}
          </Button>
          <Button variant="destructive" onClick={confirmEndForAll}>
            {confirmEnd === "webinar"
              ? t("controlBar.endWebinarForAll")
              : t("controlBar.endMeetingForAll")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
